import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import TruncatedText from "@/components/common/TruncatedText";
import { formatDateTime, formatRelativeTime } from "@/utils/dateUtils";
import { ModuleAuditEntry } from "@/types/module-kill-switch";

interface AuditTableProps {
  entries: ModuleAuditEntry[];
  isLoading: boolean;
  isError: boolean;
}

export const AuditTable: React.FC<AuditTableProps> = ({
  entries,
  isLoading,
  isError,
}) => {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-lg border border-base-border">
      <Table>
        <TableHeader>
          <TableRow className="h-10 bg-muted/40 hover:bg-muted/40">
            <TableHead className="pl-4">
              {t("modules.audit.columns.timestamp")}
            </TableHead>
            <TableHead>{t("modules.audit.columns.module")}</TableHead>
            <TableHead>{t("modules.audit.columns.direction")}</TableHead>
            <TableHead>{t("modules.audit.columns.actor")}</TableHead>
            <TableHead className="pr-4">
              {t("modules.audit.columns.reason")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center text-sm text-muted-foreground py-12"
              >
                {t("modules.page.loading")}
              </TableCell>
            </TableRow>
          )}
          {!isLoading && isError && (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center text-sm text-destructive py-12"
              >
                {t("modules.audit.error")}
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !isError && entries.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center text-sm text-muted-foreground py-12"
              >
                {t("modules.audit.empty")}
              </TableCell>
            </TableRow>
          )}
          {!isLoading &&
            !isError &&
            entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="pl-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className="text-sm text-foreground">
                      {formatDateTime(entry.createdAt)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(entry.createdAt)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {t(`modules.codes.${entry.module}.label`)}
                    </span>
                    <span className="text-xs font-mono text-muted-foreground">
                      {entry.module}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <DirectionBadge
                    previousEnabled={entry.previousEnabled}
                    nextEnabled={entry.nextEnabled}
                  />
                </TableCell>
                <TableCell>
                  <TruncatedText
                    limit={18}
                    className="text-xs font-mono text-foreground"
                  >
                    {entry.userId}
                  </TruncatedText>
                </TableCell>
                <TableCell className="pr-4 max-w-sm">
                  {entry.reason ? (
                    <TruncatedText
                      limit={80}
                      className="text-sm text-foreground break-words"
                    >
                      {entry.reason}
                    </TruncatedText>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
};

interface DirectionBadgeProps {
  previousEnabled: boolean;
  nextEnabled: boolean;
}

const DirectionBadge: React.FC<DirectionBadgeProps> = ({
  previousEnabled,
  nextEnabled,
}) => {
  const { t } = useTranslation();
  const label = (on: boolean) =>
    on ? t("modules.states.on") : t("modules.states.off");

  const variant = nextEnabled ? "success" : "new_destructive";

  return (
    <Badge variant={variant} className="gap-1 font-mono text-xs">
      {label(previousEnabled)}
      <ArrowRight className="size-3" />
      {label(nextEnabled)}
    </Badge>
  );
};
